import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtNum, fmtDate, ESTATUS_UPE, UPE_COLORS } from "../lib/format";
import { StatusBadge, ProgressBar } from "./shared";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { X, Copy, FileText, History, Sparkles, Pencil, Trash2 } from "lucide-react";

export default function UpePanel({ upeId, onClose, onChanged, onEdit }) {
  const { canEdit, isAdmin } = useAuth();
  const [upe, setUpe] = useState(null);
  const [tab, setTab] = useState("tramites");

  const load = () => api.get(`/upes/${upeId}`).then((r) => setUpe(r.data));
  useEffect(() => { setUpe(null); load(); }, [upeId]); // eslint-disable-line

  const patch = async (changes) => {
    try {
      await api.patch(`/upes/${upeId}`, changes);
      await load();
      onChanged && onChanged();
      toast.success("UPE actualizada");
    } catch (e) { toast.error(apiError(e)); }
  };

  const instanciar = async () => {
    try {
      const r = await api.post(`/upes/${upeId}/instanciar-subregimen`);
      toast.success(`Sub-régimen instanciado: ${r.data.creados} trámites (checklist de 24 pasos)`);
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };

  const eliminar = async () => {
    if (!window.confirm(`¿Eliminar la UPE "${upe.clave}" y todos sus trámites? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/upes/${upeId}`);
      toast.success("UPE eliminada");
      onChanged && onChanged();
      onClose();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!upe) return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 z-50 w-full max-w-[400px] lg:static lg:z-auto lg:w-[400px] flex-shrink-0 bg-white border-l border-slate-200 flex items-center justify-center text-slate-400 text-sm">
        Cargando ficha...
      </div>
    </>
  );

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={onClose} />
      <motion.div initial={{ x: 60, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.3 }}
        data-testid="upe-panel"
        className="fixed inset-y-0 right-0 z-50 w-full max-w-[400px] shadow-2xl lg:static lg:z-auto lg:shadow-none lg:w-[400px] flex-shrink-0 bg-white border-l border-slate-200 h-full overflow-y-auto scrollbar-thin">
      <div className="sticky top-0 bg-white border-b border-slate-200 p-5 z-10">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-sm" style={{ background: UPE_COLORS[upe.estatus] }} />
              <h3 className="font-display font-bold text-xl text-[#002B49]" data-testid="upe-panel-clave">{upe.clave}</h3>
            </div>
            <p className="text-xs text-slate-500 mt-1">Lote {upe.lote} · {upe.uso} · {fmtNum(upe.superficie_m2)} m²</p>
          </div>
          <div className="flex items-center gap-0.5">
            {canEdit && onEdit && (
              <button data-testid="upe-panel-edit" title="Editar datos de la UPE"
                onClick={() => onEdit(upe)} className="text-slate-400 hover:text-[#00A3B5] transition-colors p-1">
                <Pencil size={15} />
              </button>
            )}
            {isAdmin && (
              <button data-testid="upe-panel-delete" title="Eliminar UPE"
                onClick={eliminar} className="text-slate-400 hover:text-red-600 transition-colors p-1">
                <Trash2 size={15} />
              </button>
            )}
            <button data-testid="upe-panel-close" onClick={onClose} className="text-slate-400 hover:text-slate-700 transition-colors p-1">
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="mt-3">
          <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
            <span>Avance de tramitología</span><span className="font-bold text-[#002B49]">{upe.avance}%</span>
          </div>
          <ProgressBar value={upe.avance} />
        </div>
      </div>
      <div className="p-5 space-y-5">
        <p className="text-sm text-slate-600 leading-relaxed">{upe.descripcion}</p>
        <div className="grid grid-cols-3 gap-2 text-center">
          {[["Hotel", upe.llaves_hotel], ["Condominio", upe.llaves_condo], ["Total llaves", upe.llaves_total]].map(([l, v]) => (
            <div key={l} className="bg-slate-50 rounded-md p-2.5">
              <p className="font-display font-bold text-lg text-[#002B49]">{fmtNum(v)}</p>
              <p className="text-[10px] uppercase tracking-wider text-slate-500">{l}</p>
            </div>
          ))}
        </div>

        <div className="space-y-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Estatus de la UPE</p>
            <Select value={upe.estatus} onValueChange={(v) => patch({ estatus: v })} disabled={!canEdit}>
              <SelectTrigger data-testid="upe-estatus-select" className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ESTATUS_UPE.map((e) => (
                  <SelectItem key={e} value={e}>
                    <span className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: UPE_COLORS[e] }} />{e}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {upe.estatus_sugerido !== upe.estatus && (
              <button data-testid="upe-aplicar-sugerido" disabled={!canEdit}
                onClick={() => patch({ estatus: upe.estatus_sugerido })}
                className="mt-1.5 text-xs text-[#00A3B5] hover:underline flex items-center gap-1 disabled:opacity-50">
                <Sparkles size={11} /> Sugerido por el sistema: {upe.estatus_sugerido}
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">m² construcción existente</p>
              <Input data-testid="upe-m2-existente" type="number" defaultValue={upe.m2_construccion_existente}
                disabled={!canEdit} className="h-8 text-sm"
                onBlur={(e) => Number(e.target.value) !== upe.m2_construccion_existente && patch({ m2_construccion_existente: Number(e.target.value) })} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">m² construcción nueva</p>
              <Input data-testid="upe-m2-nueva" type="number" defaultValue={upe.m2_construccion_nueva}
                disabled={!canEdit} className="h-8 text-sm"
                onBlur={(e) => Number(e.target.value) !== upe.m2_construccion_nueva && patch({ m2_construccion_nueva: Number(e.target.value) })} />
            </div>
          </div>
          <div className="flex items-center justify-between bg-slate-50 rounded-md px-3 py-2.5">
            <p className="text-sm text-slate-700">¿Cuenta con licencia de construcción?</p>
            <Switch data-testid="upe-licencia-switch" checked={upe.licencia_construccion} disabled={!canEdit}
              onCheckedChange={(v) => patch({ licencia_construccion: v })} />
          </div>
        </div>

        {canEdit && (
          <Button data-testid="instanciar-subregimen-button" onClick={instanciar} variant="outline"
            className="w-full border-[#00A3B5] text-[#00A3B5] hover:bg-[#00A3B5]/5 gap-2 active:scale-[0.98]">
            <Copy size={14} /> Instanciar sub-régimen (24 pasos)
          </Button>
        )}

        <div className="flex gap-1 border-b border-slate-200">
          {[["tramites", `Trámites (${upe.tramites.length})`], ["docs", `Documentos (${upe.documentos.length})`], ["bitacora", "Bitácora"]].map(([k, l]) => (
            <button key={k} data-testid={`upe-tab-${k}`} onClick={() => setTab(k)}
              className={`px-3 py-2 text-xs font-semibold border-b-2 transition-colors ${tab === k ? "border-[#00A3B5] text-[#002B49]" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
              {l}
            </button>
          ))}
        </div>

        {tab === "tramites" && (
          <div className="space-y-2">
            {upe.tramites.length === 0 && <p className="text-sm text-slate-400 py-4 text-center">Sin trámites propios. Los trámites del macroproyecto aplican al lote maestro completo.</p>}
            {upe.tramites.map((t) => (
              <Link key={t.id} to={`/p/${upe.property_id}/tramites/${t.id}`} data-testid={`upe-tramite-${t.id}`}
                className="block border border-slate-200 rounded-md p-3 hover:border-[#00A3B5] transition-colors">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-slate-800 truncate">{t.nombre}</p>
                  <StatusBadge estatus={t.estatus} />
                </div>
                <ProgressBar value={t.avance ?? 0} className="mt-2" />
              </Link>
            ))}
          </div>
        )}
        {tab === "docs" && (
          <div className="space-y-2">
            {upe.documentos.length === 0 && <p className="text-sm text-slate-400 py-4 text-center">Sin documentos cargados.</p>}
            {upe.documentos.map((d) => (
              <div key={d.id} className="flex items-center gap-2 border border-slate-200 rounded-md p-2.5 text-sm">
                <FileText size={14} className="text-[#00A3B5] flex-shrink-0" />
                <span className="truncate flex-1">{d.nombre} <span className="text-slate-400">v{d.version}</span></span>
              </div>
            ))}
          </div>
        )}
        {tab === "bitacora" && (
          <div className="space-y-3">
            {upe.bitacora.length === 0 && <p className="text-sm text-slate-400 py-4 text-center">Sin movimientos registrados.</p>}
            {upe.bitacora.map((b) => (
              <div key={b.id} className="flex gap-2 text-xs">
                <History size={13} className="text-slate-300 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-slate-700"><span className="font-semibold">{b.usuario}</span> — {b.accion}</p>
                  <p className="text-slate-400">{b.entidad_nombre} · {fmtDate(b.timestamp)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </motion.div>
    </>
  );
}
